// src/components/Dashboard/StepForm.jsx
//
// Módulo `stepForm` del flujo de pasos: formulario genérico armado con los
// `fields` del paso en config/claimFlow.js. Los campos con target 'claim'
// se guardan en el reclamo (los usan LOR/SPOL y el resto de la ficha); los
// de target 'step' en stepData del paso (ej. montos del estimado en rs_pol,
// que lee la SPOL). Agregar un dato a un paso = agregarlo en la config.
// También lo usan los pasos de cada cheque (`type="check"`): ahí todos los
// campos van al stepData del cheque.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getStepData, saveStepData } from '../../services/flowService';
import { updateClaimRecordFields } from '../../services/claimRecordService';

const INPUT_TYPES = { text: 'text', email: 'email', tel: 'tel', date: 'date', number: 'number', money: 'number' };

const toFormValue = value => (value === null || value === undefined ? '' : value);
const toStoredValue = (field, value) => {
  if (field.type === 'money' || field.type === 'number') return value === '' ? null : Number(value);
  return typeof value === 'string' ? value.trim() : value;
};

const StepForm = ({ claimRecord, record: recordProp, type = 'claim', step, language, onSaved, readOnly = false }) => {
  const record = recordProp || claimRecord;
  const es = language === 'es';
  const fields = useMemo(() => step.fields || [], [step]);
  const claimKeys = useMemo(() => (type === 'claim' ? fields.filter(f => f.target === 'claim').map(f => f.key) : []), [fields, type]);
  const hasStepFields = fields.some(f => f.target !== 'claim' || type !== 'claim');
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    const fromClaim = Object.fromEntries(claimKeys.map(key => [key, toFormValue(record[key])]));
    const load = hasStepFields ? getStepData(type, record.id, step.id) : Promise.resolve({});
    load
      .catch(error => { console.error('Error loading step data:', error); return {}; })
      .then(stepData => {
        if (cancelled) return;
        const fromStep = Object.fromEntries(fields.filter(f => !claimKeys.includes(f.key)).map(f => [f.key, toFormValue(stepData[f.key])]));
        setValues({ ...fromStep, ...fromClaim });
        setLoaded(true);
      });
    return () => { cancelled = true; };
    // Se recarga al cambiar de paso o de reclamo, no en cada edición del reclamo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.id, step.id]);

  if (fields.length === 0) return null;

  const save = async event => {
    event.preventDefault();
    setSaving(true);
    setSaveError('');
    try {
      const claimChanges = {};
      const stepChanges = {};
      fields.forEach(field => {
        const stored = toStoredValue(field, values[field.key] ?? '');
        if (claimKeys.includes(field.key)) claimChanges[field.key] = stored;
        else stepChanges[field.key] = stored;
      });
      if (claimKeys.length) await updateClaimRecordFields(record.id, claimChanges, claimKeys);
      if (hasStepFields) await saveStepData(type, record.id, step.id, stepChanges);
      toast.success(es ? 'Datos del paso guardados' : 'Step data saved');
      if (onSaved) onSaved(claimChanges, stepChanges);
    } catch (error) {
      console.error('Error saving step data:', error);
      const message = es ? 'No se pudieron guardar los datos' : 'Could not save the data';
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="step-form">
      <h4 className="step-form-title">{es ? 'Datos de este paso' : 'Data for this step'}</h4>
      <div className="cc-grid">
        {fields.map(field => (
          <div className="cc-input-group" key={field.key}>
            <label className="cc-label" htmlFor={`step-${field.key}`}>
              {es ? field.label.es : field.label.en}{claimKeys.includes(field.key) ? ' ·' : ''}
            </label>
            {field.type === 'select' ? (
              <select id={`step-${field.key}`} className="cc-select" value={values[field.key] ?? ''} disabled={!loaded || readOnly}
                onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}>
                <option value="">—</option>
                {field.options.map(o => <option key={o.value} value={o.value}>{es ? o.label.es : o.label.en}</option>)}
              </select>
            ) : (
              <input id={`step-${field.key}`} className="cc-input" type={INPUT_TYPES[field.type] || 'text'} disabled={!loaded || readOnly}
                step={field.type === 'money' ? '0.01' : undefined} placeholder={field.type === 'money' ? '$' : undefined}
                value={values[field.key] ?? ''} onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))} />
            )}
          </div>
        ))}
      </div>
      {claimKeys.length > 0 && (
        <p className="step-form-note">
          {es ? '· = se guarda en los datos del reclamo (se ve en toda la ficha; algunos los usan la LOR y la SPOL).' : '· = saved on the claim record (shown across the claim; some feed the LOR and SPOL).'}
        </p>
      )}
      {saveError && <p role="alert" className="cc-field-error">{saveError}</p>}
      {!readOnly && (
        <button type="submit" className="cd-btn cd-btn-invoice" disabled={saving || !loaded}>
          <i className="fa-solid fa-floppy-disk"></i> {saving ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar datos del paso' : 'Save step data')}
        </button>
      )}
    </form>
  );
};

export default StepForm;
